//Libs
import EditIcon from "@mui/icons-material/Edit";
import EmailSharp from "@mui/icons-material/EmailSharp";
import PersonSharp from "@mui/icons-material/PersonSharp";
import PhoneSharp from "@mui/icons-material/PhoneSharp";
import PinDropSharp from "@mui/icons-material/PinDropSharp";

//Local
import * as strings from "../../strings";
import { BillingInfo } from "../../models/customer";
import { StyledTooltip } from "../StyledTooltip";
import { phoneUtils } from "../../utils/phoneUtils";
import { ExistingCustomerLocation } from "../../models/customer-location";

export function BillingInfoSection({
  billingInfo,
  onEditBillingAddress,
  customerLocations,
}: {
  billingInfo?: BillingInfo;
  onEditBillingAddress?: () => void;
  customerLocations?: ExistingCustomerLocation[] | null;
}) {
  // Count the number of locations
  const locationCount = customerLocations?.length || 0;

  // Customize warning message based on number of locations
  const getWarningMessage = () => {
    if (locationCount > 1) {
      return "This customer has multiple locations but no dedicated billing information. The service location address will be used for each job.";
    } else if (locationCount === 1) {
      return "The service location address will be used as the billing address.";
    } else {
      return "No billing information set. Please add billing details.";
    }
  };

  return (
    <div className="mb-2 mr-2 w-56 pt-4 text-base text-gray-800 md:pt-1">
      <div className="mb-2 flex flex-row items-center justify-start">
        <div className="mr-4 text-lg font-medium text-primary">
          {strings.BILLING_INFO}
        </div>

        {onEditBillingAddress && (
          <StyledTooltip title="Edit Billing Info">
            <EditIcon
              aria-label="add customer contact"
              className="h-9 w-9 shrink-0 cursor-pointer hover:text-primaryDark"
              onClick={onEditBillingAddress}
            />
          </StyledTooltip>
        )}
      </div>
      {!billingInfo && (
        <div
          className={`mt-2 rounded-md ${locationCount > 1 ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-700"} p-2 text-sm`}
        >
          <p>{getWarningMessage()}</p>
          {onEditBillingAddress && locationCount > 1 && !billingInfo && (
            <button
              onClick={onEditBillingAddress}
              className="mt-1 text-blue-600 hover:text-blue-800 hover:underline focus:outline-none"
            >
              Set up billing info
            </button>
          )}
        </div>
      )}

      {billingInfo && (
        <ul className="space-y-1">
          <li className="flex flex-row items-start">
            <PersonSharp
              sx={{ height: 18, width: 18 }}
              className="mr-1 mt-0.5 text-gray-400"
            />
            {billingInfo.name}
          </li>
          <li className="flex flex-row items-start">
            <PhoneSharp
              sx={{ height: 18, width: 18 }}
              className="mr-1 mt-0.5 text-gray-400"
            />
            {billingInfo.phone ? phoneUtils.display(billingInfo.phone) : null}
          </li>
          <li className="flex flex-row items-start">
            <EmailSharp
              sx={{ height: 18, width: 18 }}
              className="mr-1 mt-0.5 text-gray-400"
            />
            {billingInfo.email}
          </li>
          <li className="flex flex-row items-start">
            <PinDropSharp
              sx={{ height: 18, width: 18 }}
              className="mr-1 mt-0.5 text-gray-400"
            />
            <div>
              <div className="flex flex-col">
                {billingInfo.addressLine1 ?? ""}
              </div>
              {billingInfo.addressLine2 !== "" && (
                <div className="flex flex-col">
                  {billingInfo.addressLine2 ?? ""}
                </div>
              )}
              <div className="flex flex-col">
                {billingInfo.city ? `${billingInfo.city}, ` : ""}
                {billingInfo.state ?? ""} {billingInfo.zipCode ?? ""}
              </div>
            </div>
          </li>
        </ul>
      )}
    </div>
  );
}
